/**
 * SimulationHistoryContext.jsx - Decision Memory State Management
 * 
 * FOUNDER DIRECTIVE: Simulation History Timeline
 * 
 * Granullo must not only simulate decisions but also REMEMBER them.
 * 
 * Narrative: decision → propagation → consequence → memory
 * 
 * This context manages:
 * - Simulation history records
 * - Cascade replay state
 * - Timeline navigation
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Context
const SimulationHistoryContext = createContext(null);

// Generate unique ID
const generateId = () => `sim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Provider Component
export function SimulationHistoryProvider({ children }) {
  // Load history from localStorage
  const [history, setHistory] = useState(() => {
    try {
      const stored = localStorage.getItem('granullo_simulation_history');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Currently selected simulation for replay
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  
  // Replay state
  const [replayState, setReplayState] = useState({
    isReplaying: false,
    progress: 0,
    currentStep: null
  });

  // Persist history to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('granullo_simulation_history', JSON.stringify(history.slice(0, 50))); // Keep last 50
    } catch {}
  }, [history]);

  // Record a new simulation
  const recordSimulation = useCallback((simulationData) => {
    const record = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      trigger: simulationData.trigger || 'Manual',
      scenario: simulationData.scenario || 'Custom Scenario',
      params: simulationData.params || {},
      cascadePath: simulationData.cascadePath || [],
      affectedNodes: simulationData.affectedNodes || [],
      outcome: simulationData.outcome || {},
      metrics: {
        coverageDelta: simulationData.metrics?.coverageDelta || 0,
        healthDelta: simulationData.metrics?.healthDelta || 0,
        riskLevel: simulationData.metrics?.riskLevel || 'Low'
      }
    };

    setHistory(prev => [record, ...prev]);
    return record;
  }, []);

  // Select a simulation for replay
  const selectSimulation = useCallback((simulationId) => {
    const simulation = history.find(s => s.id === simulationId);
    setSelectedSimulation(simulation || null);
    setReplayState({
      isReplaying: false,
      progress: 0,
      currentStep: null
    });
  }, [history]);

  // Start cascade replay
  const startReplay = useCallback(() => {
    if (!selectedSimulation) return;

    setReplayState({
      isReplaying: true,
      progress: 0,
      currentStep: 0
    });

    // Animate through cascade steps
    const steps = selectedSimulation.cascadePath.length || 5;
    let currentStep = 0;
    
    const interval = setInterval(() => {
      currentStep++;
      const progress = (currentStep / steps) * 100;
      
      if (currentStep >= steps) {
        clearInterval(interval);
        setReplayState({
          isReplaying: false,
          progress: 100,
          currentStep: steps
        });
      } else {
        setReplayState({
          isReplaying: true,
          progress,
          currentStep
        });
      }
    }, 300);

    return () => clearInterval(interval);
  }, [selectedSimulation]);

  // Stop replay
  const stopReplay = useCallback(() => {
    setReplayState({
      isReplaying: false,
      progress: 0,
      currentStep: null
    });
  }, []);

  // Clear history
  const clearHistory = useCallback(() => {
    setHistory([]);
    setSelectedSimulation(null);
    try {
      localStorage.removeItem('granullo_simulation_history');
    } catch {}
  }, []);

  // Get recent simulations
  const getRecentSimulations = useCallback((limit = 10) => {
    return history.slice(0, limit);
  }, [history]);

  const value = {
    // State
    history,
    selectedSimulation,
    replayState,
    
    // Actions
    recordSimulation,
    selectSimulation,
    startReplay,
    stopReplay,
    clearHistory,
    getRecentSimulations,
    
    // Derived
    hasHistory: history.length > 0,
    historyCount: history.length
  };

  return (
    <SimulationHistoryContext.Provider value={value}>
      {children}
    </SimulationHistoryContext.Provider>
  );
}

// Hook to use Simulation History
export function useSimulationHistory() {
  const context = useContext(SimulationHistoryContext);
  if (!context) {
    // Return safe defaults if not in provider
    return {
      history: [],
      selectedSimulation: null,
      replayState: { isReplaying: false, progress: 0, currentStep: null },
      recordSimulation: () => {},
      selectSimulation: () => {},
      startReplay: () => {},
      stopReplay: () => {},
      clearHistory: () => {},
      getRecentSimulations: () => [],
      hasHistory: false,
      historyCount: 0
    };
  }
  return context;
}

export default SimulationHistoryContext;
